import React, { useState } from "react";
import { useLang } from "../lib/LangContext";
import { testimonials } from "../lib/i18n";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";

const Testimonials = () => {
  const { t, lang } = useLang();
  const [idx, setIdx] = useState(0);
  const total = testimonials.length;

  const go = (dir) => setIdx((i) => (i + dir + total) % total);

  const active = testimonials[idx];
  const quote = lang === "id" ? active.quoteId : active.quoteEn;

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative bg-[#0a0a0a] py-20 md:py-28 overflow-hidden"
    >
      {/* decorative checker accent */}
      <div className="absolute top-0 right-0 w-32 h-8 checker opacity-30 pointer-events-none" />

      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-8 bg-brand-yellow" />
              <span className="text-[11px] uppercase tracking-[0.4em] text-brand-yellow font-semibold">
                {t.testimonials.eyebrow}
              </span>
            </div>
            <h2 className="font-anton uppercase text-white leading-[0.95]" style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)" }}>
              {t.testimonials.title}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="testimonial-prev"
              onClick={() => go(-1)}
              className="w-11 h-11 border border-white/20 text-white hover:bg-brand-yellow hover:text-black hover:border-brand-yellow transition-colors flex items-center justify-center"
              aria-label="previous"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              data-testid="testimonial-next"
              onClick={() => go(1)}
              className="w-11 h-11 border border-white/20 text-white hover:bg-brand-yellow hover:text-black hover:border-brand-yellow transition-colors flex items-center justify-center"
              aria-label="next"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] w-full max-w-sm bg-black overflow-hidden border border-brand-yellow/20">
              <img
                key={active.avatar}
                src={active.avatar}
                alt={active.name}
                className="absolute inset-0 w-full h-full object-cover fade-up"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className="font-anton uppercase text-xl md:text-2xl text-white">{active.name}</div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-brand-yellow mt-1">
                  {active.handle}
                </div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/60 mt-1">
                  {active.role}
                </div>
              </div>
              <div className="absolute top-3 right-3 bg-brand-yellow text-black px-2 py-1 text-[10px] uppercase tracking-widest font-bold">
                {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
              </div>
            </div>
          </div>

          <div data-testid="testimonial-quote" className="lg:col-span-7">
            <Quote size={44} className="text-brand-yellow mb-4" strokeWidth={1.2} />
            <p
              key={quote}
              className="font-anton uppercase text-white leading-tight tracking-tight fade-up"
              style={{ fontSize: "clamp(1.5rem, 3vw, 2.5rem)" }}
            >
              {quote}
            </p>
            <div className="mt-8 flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  data-testid={`testimonial-dot-${i}`}
                  onClick={() => setIdx(i)}
                  className={`h-1 transition-all ${
                    i === idx ? "w-10 bg-brand-yellow" : "w-5 bg-white/20 hover:bg-white/40"
                  }`}
                  aria-label={`go to ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
